import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

const FIVE_MINUTES = 1000 * 60 * 5;

export interface UtilizationReportFilters {
  start_date?: string;
  end_date?: string;
}

export interface UtilizationReportItem {
  asset_id: number;
  asset_name: string;
  serial_number: string;
  utilization_percentage: number;
  deployed_days: number;
  idle_days: number;
}

export interface UtilizationReportData {
  results: UtilizationReportItem[];
  start_date: string;
  end_date: string;
  total_assets: number;
}

export function useUtilizationReport(filters: UtilizationReportFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.reports.utilization(cleanFilters),
    queryFn: async (): Promise<UtilizationReportData> => {
      const response = await apiClient.get<UtilizationReportData>('/api/v1/reports/utilization/', {
        params: cleanFilters,
      });
      return response.data;
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}

export interface InspectionComplianceFilters {
  start_date?: string;
  end_date?: string;
}

export interface InspectionComplianceData {
  total_inspections: number;
  overdue_count: number;
  completed_count: number;
  pass_count: number;
  fail_count: number;
  conditional_count: number;
  pass_rate: number;
  by_result: {
    pass: number;
    fail: number;
    conditional: number;
  };
}

export function useInspectionComplianceReport(filters: InspectionComplianceFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.reports.inspectionCompliance(cleanFilters),
    queryFn: async (): Promise<InspectionComplianceData> => {
      const response = await apiClient.get<InspectionComplianceData>(
        '/api/v1/reports/inspection-compliance/',
        { params: cleanFilters }
      );
      return response.data;
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}

export interface RopeCutTrendsFilters {
  start_date?: string;
  end_date?: string;
}

export interface RopeCutTrendItem {
  month: number;
  year: number;
  count: number;
  total_cut_length: number;
}

export interface RopeCutDetailItem {
  id: number;
  date: string;
  asset_id: number;
  asset_name: string;
  serial_number: string;
  cut_length: number;
  remaining_length: number;
  performed_by: string;
  notes: string;
}

export interface RopeCutTrendsData {
  trends: RopeCutTrendItem[];
  cuts: RopeCutDetailItem[];
  total_cuts: number;
  total_length_removed: number;
  start_date: string;
  end_date: string;
}

export function useRopeCutTrendsReport(filters: RopeCutTrendsFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.reports.ropeCutTrends(cleanFilters),
    queryFn: async (): Promise<RopeCutTrendsData> => {
      const response = await apiClient.get<RopeCutTrendsData>('/api/v1/reports/ropecut-trends/', {
        params: cleanFilters,
      });
      return response.data;
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}

export interface GearForecastFilters {
  q?: string;
  status?: string;
  manufacturer?: string;
  bucket?: string;
  due_type?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface GearForecastItem {
  id: number;
  serial_number: string;
  manufacturer: string;
  model: string;
  type: string;
  status: string;
  due_type: string;
  due_date: string;
  days_until_due: number;
  bucket: string;
}

export interface GearForecastData {
  counts_by_bucket: Record<string, number>;
  counts_by_due_type: Record<string, number>;
  total: number;
  results: GearForecastItem[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export function useGearForecastReport(filters: GearForecastFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.reports.gearForecast(cleanFilters),
    queryFn: async (): Promise<GearForecastData> => {
      const response = await apiClient.get<GearForecastData>(
        '/api/v1/reports/gear-forecast/',
        { params: cleanFilters }
      );
      return response.data;
    },
    refetchInterval: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });
}

export function useExportUtilizationCSV() {
  const exportCSV = async (filters: UtilizationReportFilters = {}): Promise<Blob> => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
    );

    const response = await apiClient.get('/api/v1/reports/utilization/', {
      params: cleanFilters,
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
      },
    });
    return response.data;
  };

  return { exportCSV };
}

export function useExportInspectionComplianceCSV() {
  const exportCSV = async (filters: InspectionComplianceFilters = {}): Promise<Blob> => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
    );

    const response = await apiClient.get('/api/v1/reports/inspection-compliance/', {
      params: cleanFilters,
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
      },
    });
    return response.data;
  };

  return { exportCSV };
}

export function useExportRopeCutTrendsCSV() {
  const exportCSV = async (filters: RopeCutTrendsFilters = {}): Promise<Blob> => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
    );

    const response = await apiClient.get('/api/v1/reports/ropecut-trends/', {
      params: cleanFilters,
      responseType: 'blob',
      headers: {
        Accept: 'text/csv',
      },
    });
    return response.data;
  };

  return { exportCSV };
}
